import { CommonModule } from '@angular/common';
import { CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { showErrorSwal } from '@asamco/utils';
import Swal from 'sweetalert2';
import { DxButtonModule, DxDataGridModule, DxDateBoxModule } from 'devextreme-angular';
import { SelectionChangedEvent } from 'devextreme/ui/data_grid';
import { firstValueFrom } from 'rxjs';
import {
	BulkUpdateChangelogReleaseDto,
	ChangelogDetailDto,
	ChangelogFragmentDto,
	ChangelogItemDto,
	ChangelogReleaseEditorDto,
	ChangelogReleaseOverviewDto,
	ChangelogReleasePreviewDto,
	ChangelogReleaseSourceDto,
	ChangelogService,
	ChangelogTargetApplicationOptionDto,
	ChangelogValidationWarningDto,
	GroupedChangelogItemDto,
	GroupedChangelogPayloadDto,
	GroupedChangelogSectionDto,
	GroupedChangelogSourceReferenceDto,
	MergeChangelogReleasePreviewRequestDto,
	MergeChangelogReleaseRequestDto,
} from '../../../services/changelog.service';
import { AdminWarningBannerComponent } from '../admin-warning-banner.component';
import { TourOverlayComponent } from '../../../tour/tour-overlay.component';
import { TourService, TourStep } from '../../../services/tour.service';

type ChangelogReleaseOverviewRow = ChangelogReleaseOverviewDto & {
	selectionKey: string;
	displayTitle: string;
	conversionState: string;
	conversionHint: string;
	workflowStatusText: string;
	sourceApplicationsText: string;
	highlightsText: string;
};

type ReleaseSelectionSummary = {
	selectionKey: string;
	displayTitle: string;
	releaseFamilyKey: string;
	version: string;
	isPreRelease: boolean;
	sourceCount: number;
};

type ResolvedSourceReferenceViewModel = {
	key: string;
	sourceLabel: string;
	versionLabel: string;
	locationLabel: string;
	description?: string | null;
	sourceFile?: string | null;
	isLoading: boolean;
};

type WorkflowStepId = 'details' | 'notes' | 'preview';

type WorkflowStepViewModel = {
	id: WorkflowStepId;
	label: string;
	title: string;
	helper: string;
	isActive: boolean;
	isComplete: boolean;
	isBlocked: boolean;
};

type WorkflowModalStepId = 'select' | 'review' | 'confirm';

type SourceFileWorkflowItem = {
	itemId: string;
	sectionName: string;
	text: string;
	originalText: string;
	visibility: string;
	scope: string;
	targetApplicationKeys: string[];
	isManuallyEdited: boolean;
	included: boolean;
	sourceReferences: GroupedChangelogSourceReferenceDto[];
};

type SourceFileWorkflowGroup = {
	key: string;
	sourceChangelogId: number;
	sourceApplicationKey: string;
	sourceApplicationName: string;
	versionLabel: string;
	sourceFile: string;
	fileName: string;
	pointCount: number;
	sectionSummary: string;
	items: SourceFileWorkflowItem[];
};

type ApplicationSelectableItem = {
	targetApplicationKeys: string[];
	scope: string;
};

@Component({
	selector: 'app-shell-admin-changelog-management',
	standalone: true,
	imports: [
		AdminWarningBannerComponent,
		CommonModule,
		DragDropModule,
		FormsModule,
		TourOverlayComponent,
		DxButtonModule,
		DxDataGridModule,
		DxDateBoxModule,
	],
	templateUrl: './admin-changelog-management.component.html',
	styleUrls: ['./admin-changelog-management.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminChangelogManagementComponent implements OnInit {
	private readonly overviewTake = 50;
	private readonly sourceDetailsById = new Map<number, ChangelogDetailDto>();
	private readonly loadingSourceDetailIds = new Set<number>();
	private readonly sourceDetailLoadFailures = new Set<number>();

	public overviewRows: ChangelogReleaseOverviewRow[] = [];
	public selectedRowKeys: string[] = [];
	public pendingOverviewSelections: ReleaseSelectionSummary[] = [];
	public confirmedOverviewSelections: ReleaseSelectionSummary[] = [];
	public isReleaseBrowserVisible = true;
	public isSelectingRelease = false;

	public selectedOverview?: ReleaseSelectionSummary;
	public release?: ChangelogReleaseEditorDto;
	public editorPayload?: GroupedChangelogPayloadDto;
	public preview?: ChangelogReleasePreviewDto;


	public previewAudience = 'Users';
	public previewStage = 'Draft';
	public isWorkflowModalOpen = false;
	public workflowModalStep: WorkflowModalStepId = 'select';
	public itemApplicationSelectorItem?: ApplicationSelectableItem;
	public itemApplicationSelectorMode: 'editor' | 'workflow' | null = null;
	public draftItemApplicationKeys: string[] = [];
	public itemApplicationSearch = '';
	public selectedSourceIds: number[] = [];
	public selectedSourceFileKeys: string[] = [];
	public sourceFileSearch = '';
	public workflowSourceFileGroups: SourceFileWorkflowGroup[] = [];
	public workflowReviewFileIndex = 0;
	public hasCompletedWorkflowDrafting = false;
	public collapsedSectionIndices = new Set<number>();
	public isDraftItemsCompact = true;
	private readonly expandedDraftItemIds = new Set<string>();
	public draggingDraftItemId?: string;

	public selectedBulkItemIds: string[] = [];
	public bulkSectionName = '';
	public bulkVisibility = '';
	public bulkScope = '';
	public bulkTargetApplicationKeys: string[] = [];

	public loadingOverview = false;
	public loadingEntry = false;
	public loadingPreview = false;
	public saving = false;
	public applyingBulkUpdate = false;
	public isDirty = false;

	constructor(
		private changelogService: ChangelogService,
		private changeDetectorRef: ChangeDetectorRef,
		private tour: TourService,
	) {}

	ngOnInit(): void {
		void this.loadOverview();
	}

	public get hasSelection(): boolean {
		return this.selectedOverview != null && this.release != null && this.editorPayload != null;
	}

	public get canOpenSelectedRelease(): boolean {
		return this.pendingOverviewSelections.length > 0 && !this.loadingOverview && !this.loadingEntry;
	}

	public get pendingOverviewCount(): number {
		return this.overviewRows.filter(row => !row.exists).length;
	}

	public get convertedOverviewCount(): number {
		return this.overviewRows.length - this.pendingOverviewCount;
	}

	public get confirmedSelectionCount(): number {
		return this.confirmedOverviewSelections.length;
	}

	public get canApplyBulkUpdate(): boolean {
		return this.hasSelection
			&& !this.isDirty
			&& !this.applyingBulkUpdate
			&& this.selectedBulkItemIds.length > 0
			&& (!!this.bulkSectionName || !!this.bulkVisibility || !!this.bulkScope || this.bulkTargetApplicationKeys.length > 0);
	}

	public get allowedSections(): string[] {
		return this.release?.allowedSections ?? ['Added', 'Changed', 'Fixed', 'Removed', 'Deprecated', 'Security', 'Notes'];
	}

	public get allowedPreviewAudiences(): string[] {
		return this.release?.allowedPreviewAudiences ?? ['Users', 'Superadmins'];
	}

	public get availableApplications(): ChangelogTargetApplicationOptionDto[] {
		return this.release?.availableApplications ?? [];
	}

	public get workflowSteps(): WorkflowStepViewModel[] {
		const activeStep = this.activeStep;
		const hasReleaseDetails = this.hasReleaseDetails;
		const hasDraftNotes = this.totalDraftItems > 0;
		const canPreview = this.canPreview;

		return [
			{
				id: 'details',
				label: '1',
				title: 'Release Details',
				helper: hasReleaseDetails ? 'Release date and title are ready.' : 'Set the release date and confirm the title before editing notes.',
				isActive: activeStep === 'details',
				isComplete: hasReleaseDetails,
				isBlocked: false,
			},
			{
				id: 'notes',
				label: '2',
				title: 'Edit Notes',
				helper: !hasReleaseDetails
					? 'Unlocks after release details are complete.'
					: hasDraftNotes
						? `${this.totalDraftItems} points currently in the draft.`
						: 'Build or add the release points you want to publish.',
				isActive: activeStep === 'notes',
				isComplete: hasDraftNotes,
				isBlocked: !hasReleaseDetails,
			},
			{
				id: 'preview',
				label: '3',
				title: 'Preview & Publish',
				helper: !hasReleaseDetails
					? 'Complete the release details first.'
					: !hasDraftNotes
						? 'Add at least one release note to unlock preview.'
						: this.isDirty
							? 'Save first to preview the latest changes.'
							: 'Validate the output before publishing.',
				isActive: activeStep === 'preview',
				isComplete: canPreview && !this.isDirty && this.preview != null,
				isBlocked: !canPreview,
			},
		];
	}

	public get sourceFileGroups(): SourceFileWorkflowGroup[] {
		return this.workflowSourceFileGroups;
	}

	public get filteredSourceFileGroups(): SourceFileWorkflowGroup[] {
		const search = this.sourceFileSearch.trim().toLocaleLowerCase();
		if (!search) {
			return this.sourceFileGroups;
		}

		return this.sourceFileGroups.filter(group =>
			group.fileName.toLocaleLowerCase().includes(search)
			|| group.sourceFile.toLocaleLowerCase().includes(search)
			|| group.sourceApplicationName.toLocaleLowerCase().includes(search)
			|| group.sourceApplicationKey.toLocaleLowerCase().includes(search)
			|| group.items.some(item =>
				item.text.toLocaleLowerCase().includes(search)
				|| item.sectionName.toLocaleLowerCase().includes(search),
			),
		);
	}

	public get totalSourceFileCount(): number {
		return this.sourceFileGroups.length;
	}

	public get selectedSourceFileCount(): number {
		const selectedKeySet = new Set(this.selectedSourceFileKeys);
		return this.sourceFileGroups.filter(group => selectedKeySet.has(group.key)).length;
	}

	public get selectedSourceFilePointCount(): number {
		const selectedKeySet = new Set(this.selectedSourceFileKeys);
		const pointIds = new Set<string>();

		for (const group of this.sourceFileGroups) {
			if (!selectedKeySet.has(group.key)) {
				continue;
			}

			for (const item of group.items) {
				pointIds.add(item.itemId);
			}
		}

		return pointIds.size;
	}

	public get sourceFileWorkflowSummaryText(): string {
		if (this.totalSourceFileCount === 0) {
			return 'No source files found in this release.';
		}

		const fileText = this.selectedSourceFileCount === 1
			? '1 file selected'
			: `${this.selectedSourceFileCount} files selected`;
		const pointText = this.selectedSourceFilePointCount === 1
			? '1 grouped point'
			: `${this.selectedSourceFilePointCount} grouped points`;
		return `${fileText} across ${pointText}`;
	}

	public get filteredItemApplications(): ChangelogTargetApplicationOptionDto[] {
		const search = this.itemApplicationSearch.trim().toLocaleLowerCase();
		if (!search) {
			return this.availableApplications;
		}

		return this.availableApplications.filter(application =>
			application.displayName.toLocaleLowerCase().includes(search)
			|| application.applicationKey.toLocaleLowerCase().includes(search),
		);
	}

	public get releaseDisplayTitle(): string {
		return this.getPayloadDisplayTitle(this.editorPayload);
	}

	public get previewDisplayTitle(): string {
		return this.getPayloadDisplayTitle(this.currentPreviewPayload);
	}

	public get defaultReleaseTitle(): string {
		return this.getDefaultReleaseTitle(this.editorPayload?.releaseDate);
	}

	public get currentPreviewPayload(): GroupedChangelogPayloadDto | null {
		const payload = this.getBasePreviewPayload();
		if (payload == null) {
			return null;
		}

		return this.createFilteredPreviewPayload(payload);
	}

	public get currentPreviewWarnings(): ChangelogValidationWarningDto[] {
		const payload = this.getBasePreviewPayload();
		if (payload == null) {
			return this.preview?.validationWarnings ?? [];
		}

		return this.getValidationWarningsForPayload(payload, this.release?.status ?? 'Draft');
	}

	public get previewSyncButtonText(): string {
		return 'Re-run Warnings';
	}

	public get previewSyncMessage(): string {
		return this.isDirty
			? 'This panel updates instantly from your current edits. Warning checks below are based on the current draft. Save Draft if you want to re-run the backend warning pass.'
			: 'This panel is in sync with the saved draft. Use Re-run Warnings to refresh the backend warning pass for the saved release.';
	}

	public get selectedSourceCount(): number {
		return this.selectedSourceIds.length;
	}

	public get savedSourceCount(): number {
		return this.release?.sources.length ?? 0;
	}

	public get totalDraftItems(): number {
		return this.editorPayload?.sections.reduce((total, section) => total + section.items.length, 0) ?? 0;
	}

	public get userVisibleDraftItems(): number {
		return this.editorPayload?.sections.reduce((total, section) => total + section.items.filter(item => item.visibility === 'Users').length, 0) ?? 0;
	}

	public get applicationScopedDraftItems(): number {
		return this.editorPayload?.sections.reduce((total, section) => total + section.items.filter(item => item.scope === 'Application').length, 0) ?? 0;
	}

	public get isSummaryBlank(): boolean {
		return !this.editorPayload?.summary?.trim();
	}

	public get hasReleaseDetails(): boolean {
		return !!this.editorPayload?.releaseDate?.trim() && !!this.editorPayload?.title?.trim();
	}

	public get isNotesStepBlocked(): boolean {
		return !this.hasReleaseDetails;
	}

	public get canPreview(): boolean {
		return this.hasReleaseDetails && this.totalDraftItems > 0;
	}

	public get isPreviewStepBlocked(): boolean {
		return !this.canPreview;
	}

	public get releaseDateValue(): Date | null {
		return this.parseReleaseDate(this.editorPayload?.releaseDate);
	}

	public get activeStep(): WorkflowStepId {
		if (!this.hasReleaseDetails) {
			return 'details';
		}

		if (this.totalDraftItems === 0) {
			return 'notes';
		}

		return 'preview';
	}

	public get activeStepTitle(): string {
		switch (this.activeStep) {
			case 'details':
				return 'Add the release framing details';
			case 'notes':
				return 'Review and edit the generated release notes';
			default:
				return this.isDirty ? 'Save Draft and sync warnings' : 'Preview and publish';
		}
	}

	public get canStartWorkflowReview(): boolean {
		return this.selectedSourceFileCount > 0;
	}

	public get isMainEditorBlocked(): boolean {
		return this.hasSelection
			&& !this.hasCompletedWorkflowDrafting
			&& this.totalSourceFileCount > 0;
	}

	public get workflowSelectedSourceFileGroups(): SourceFileWorkflowGroup[] {
		const selectedKeySet = new Set(this.selectedSourceFileKeys);
		return this.sourceFileGroups.filter(group => selectedKeySet.has(group.key));
	}

	public get currentWorkflowReviewGroup(): SourceFileWorkflowGroup | undefined {
		return this.workflowSelectedSourceFileGroups[this.workflowReviewFileIndex];
	}

	public get workflowIncludedItemCount(): number {
		return this.workflowSelectedSourceFileGroups.reduce((total, group) => total + group.items.filter(item => item.included).length, 0);
	}

	public get workflowConfirmSections(): GroupedChangelogSectionDto[] {
		const includedItems = this.workflowSelectedSourceFileGroups.reduce((entries, group) => {
			for (const item of group.items) {
				if (!item.included) {
					continue;
				}

				entries.push({
					sectionName: item.sectionName,
					item: {
						id: item.itemId,
						text: item.text,
						visibility: item.visibility,
						scope: item.scope,
						targetApplicationKeys: [...item.targetApplicationKeys],
						isManuallyEdited: item.isManuallyEdited || item.text.trim() !== item.originalText.trim(),
						sourceReferences: item.sourceReferences.map(reference => ({ ...reference })),
					} as GroupedChangelogItemDto,
				});
			}

			return entries;
		}, [] as { sectionName: string; item: GroupedChangelogItemDto }[]);
		const grouped = includedItems.reduce((sections, entry) => {
			const existingSection = sections.find(section => section.sectionName === entry.sectionName);
			if (existingSection != null) {
				existingSection.items.push(entry.item);
			} else {
				sections.push({ sectionName: entry.sectionName, items: [entry.item] });
			}
			return sections;
		}, [] as GroupedChangelogSectionDto[]);

		return this.sortSections(grouped).map(section => ({
			sectionName: section.sectionName,
			items: section.items,
		}));
	}


	public async loadOverview(preferredSelectionKeys?: string[]): Promise<void> {
		this.loadingOverview = true;
		this.changeDetectorRef.markForCheck();

		try {
			const overview = await firstValueFrom(this.changelogService.getReleaseOverview({
				take: this.overviewTake,
			}));

			this.overviewRows = overview.map(entry => this.toOverviewRow(entry));

			if (this.overviewRows.length === 0) {
				this.clearSelection();
				return;
			}

			const selectionKeys = preferredSelectionKeys
				?? (this.pendingOverviewSelections.length > 0
					? this.pendingOverviewSelections.map(selection => selection.selectionKey)
					: this.confirmedOverviewSelections.map(selection => selection.selectionKey));
			const nextSelections = selectionKeys.length > 0
				? this.getSelectionSummariesByKeys(selectionKeys)
				: this.getLatestSelectionSummaries();
			this.pendingOverviewSelections = nextSelections;
			this.selectedRowKeys = nextSelections.map(selection => selection.selectionKey);

			if (this.confirmedOverviewSelections.length > 0 && nextSelections.length === 0) {
				this.clearSelection();
			}
		} catch (error) {
			showErrorSwal(error);
		} finally {
			this.loadingOverview = false;
			this.changeDetectorRef.markForCheck();
		}
	}

	public onOverviewSelectionChanged(event: SelectionChangedEvent): void {
		const selectedRowKeys = ((event.selectedRowKeys ?? []) as string[])
			.filter((value): value is string => typeof value === 'string');
		this.selectedRowKeys = selectedRowKeys;
		this.pendingOverviewSelections = this.getSelectionSummariesByKeys(selectedRowKeys);
		this.changeDetectorRef.markForCheck();
	}

	public selectLatestOverviewGroup(): void {
		const latestSelections = this.getLatestSelectionSummaries();
		this.pendingOverviewSelections = latestSelections;
		this.selectedRowKeys = latestSelections.map(selection => selection.selectionKey);
		this.changeDetectorRef.markForCheck();
	}

	public async refreshSelectedRelease(): Promise<void> {
		if (this.selectedOverview == null) {
			return;
		}

		if (this.isDirty && !this.confirmDiscardChanges()) {
			return;
		}

		const didLoad = this.confirmedSelectionCount > 1
			? await this.loadMergedRelease(this.confirmedOverviewSelections, false)
			: await this.loadSingleRelease(this.selectedOverview, false);
		if (!didLoad) {
			return;
		}
	}

	public async openSelectedRelease(): Promise<void> {
		const selections = [...this.pendingOverviewSelections];
		if (selections.length === 0) {
			return;
		}

		const selectionKeys = selections.map(selection => selection.selectionKey);
		const previousKeys = this.confirmedOverviewSelections.map(selection => selection.selectionKey);
		if (!this.areSelectionKeysEqual(selectionKeys, previousKeys) && this.isDirty && !this.confirmDiscardChanges()) {
			this.pendingOverviewSelections = [...this.confirmedOverviewSelections];
			this.selectedRowKeys = this.confirmedOverviewSelections.map(selection => selection.selectionKey);
			this.changeDetectorRef.markForCheck();
			return;
		}

		if (!this.confirmOpenRelease(selections)) {
			return;
		}

		const didLoad = selections.length === 1
			? await this.loadSingleRelease(selections[0], true)
			: await this.loadMergedRelease(selections, true);
		if (!didLoad) {
			return;
		}

		this.confirmedOverviewSelections = selections;
		this.pendingOverviewSelections = selections;
		this.selectedRowKeys = selectionKeys;
		this.isReleaseBrowserVisible = false;
		this.isSelectingRelease = false;
		this.changeDetectorRef.markForCheck();
	}

	public showReleaseBrowser(): void {
		this.isReleaseBrowserVisible = true;
		this.pendingOverviewSelections = [...this.confirmedOverviewSelections];
		this.selectedRowKeys = this.confirmedOverviewSelections.map(selection => selection.selectionKey);
		this.changeDetectorRef.markForCheck();
	}

	public returnToReleaseSelection(): void {
		if (this.isDirty && !this.confirmDiscardChanges()) {
			return;
		}

		this.isReleaseBrowserVisible = true;
		this.isSelectingRelease = true;
		this.pendingOverviewSelections = [...this.confirmedOverviewSelections];
		this.selectedRowKeys = this.confirmedOverviewSelections.map(selection => selection.selectionKey);
		this.changeDetectorRef.markForCheck();
	}


	public startTour(): void {
		const steps: TourStep[] = [
			{
				id: 'release-browser',
				target: '#tour-release-browser',
				title: 'Choose the release set',
				body: 'Start here by refreshing the list, selecting one or more exports, and opening the grouped release you want to edit.',
				placement: 'right',
				padding: 10,
				beforeShow: async () => {
					this.showReleaseBrowser();
					this.changeDetectorRef.detectChanges();
				},
			},
			{
				id: 'browser-actions',
				target: '#tour-browser-actions',
				title: 'Refresh and merge',
				body: 'Use Refresh List to reload recent exports. After selecting rows, Merge Selected opens them in the grouped changelog editor.',
				placement: 'bottom',
				padding: 10,
				beforeShow: async () => {
					this.showReleaseBrowser();
					this.changeDetectorRef.detectChanges();
				},
			},
		];

		if (this.hasSelection) {
			steps.push(
				{
					id: 'workflow-strip',
					target: '.workflow-strip',
					title: 'Follow the workflow',
					body: 'Most of the curation now happens in the Source Workflow modal. After that, this screen is mainly for final framing, small edits, and previewing.',
					placement: 'bottom',
					padding: 10,
				},
				{
					id: 'editor-hero',
					target: '#tour-editor-hero',
					title: 'Current grouped release',
					body: 'This header shows the grouped release you are editing and gives you the main save, preview, ready, publish, and archive actions.',
					placement: 'bottom',
					padding: 10,
				},
				{
					id: 'release-details',
					target: '#release-details',
					title: 'Release details',
					body: 'Update the release date, optional custom title, summary, and source applications that describe this grouped export.',
					placement: 'right',
					padding: 10,
				},
				{
					id: 'release-notes',
					target: '#release-notes',
					title: 'Edit the draft notes',
					body: 'Once the right files are selected, refine the grouped points here, add manual notes, and adjust application scope where needed.',
					placement: 'right',
					padding: 10,
				},
				{
					id: 'release-preview',
					target: '#release-preview',
					title: 'Preview the final output',
					body: 'This panel updates as you edit. After saving, use Re-run Warnings to refresh the backend warning checks for the saved release.',
					placement: 'left',
					padding: 10,
				},
			);
		}

		this.tour.start(steps, 0);
	}
	public async saveDraft(): Promise<void> {
		await this.saveRelease('Draft');
	}

	public async markReadyForReview(): Promise<void> {
		await this.saveRelease('ReadyForReview');
	}

	public async publish(): Promise<void> {
		await this.saveRelease('Published');
	}

	public async archive(): Promise<void> {
		await this.saveRelease('Archived');
	}

	public async refreshPreview(): Promise<void> {
		if (this.selectedOverview == null) {
			return;
		}

		this.loadingPreview = true;
		this.changeDetectorRef.markForCheck();

		try {
			const preview = this.confirmedSelectionCount > 1
				? await firstValueFrom(this.changelogService.getMergedReleasePreview(this.createMergedReleasePreviewRequest(this.confirmedOverviewSelections)))
				: await firstValueFrom(this.changelogService.getReleasePreview(
					this.selectedOverview.releaseFamilyKey,
					this.selectedOverview.version,
					this.selectedOverview.isPreRelease,
					{
						audience: this.previewAudience,
						stage: this.previewStage,
					},
				));

			this.preview = {
				...preview,
				payload: this.clonePayload(preview.payload),
			};
		} catch (error) {
			showErrorSwal(error);
		} finally {
			this.loadingPreview = false;
			this.changeDetectorRef.markForCheck();
		}
	}

	public resetDraftToSuggested(): void {
		if (this.release == null) {
			return;
		}

		this.editorPayload = this.normalizeEditorPayload(
			this.clonePayload(this.release.suggestedPayload),
			this.release.suggestedPayload,
		);
		this.initializeSourceFileSelection();
		this.refreshWorkflowDraftingState();
		this.isDirty = true;
		this.selectedBulkItemIds = [];
		this.changeDetectorRef.markForCheck();
	}

	public restorePublishedToDraft(): void {
		if (this.release?.publishedPayload == null) {
			return;
		}

		this.editorPayload = this.normalizeEditorPayload(
			this.clonePayload(this.release.publishedPayload),
			this.release.suggestedPayload,
		);
		this.initializeSourceFileSelection();
		this.refreshWorkflowDraftingState();
		this.isDirty = true;
		this.selectedBulkItemIds = [];
		this.changeDetectorRef.markForCheck();
	}

	public async addSection(): Promise<void> {
		if (this.editorPayload == null) {
			return;
		}

		if (this.allowedSections.length === 0) {
			await Swal.fire({
				icon: 'error',
				title: 'No sections available',
				text: 'There are no section types available to add.',
			});
			return;
		}

		const result = await Swal.fire({
			title: 'Add section',
			text: 'Choose which section you want to add.',
			input: 'select',
			inputOptions: this.allowedSections.reduce<Record<string, string>>((options, sectionName) => {
				options[sectionName] = sectionName;
				return options;
			}, {}),
			inputPlaceholder: 'Select a section',
			showCancelButton: true,
			confirmButtonText: 'Add section',
			inputValidator: value => value ? undefined : 'Please select a section',
		});

		const nextSectionName = result.value;
		if (!result.isConfirmed || !nextSectionName) {
			return;
		}

		const alreadyExists = this.editorPayload.sections.some(section => section.sectionName === nextSectionName);
		if (alreadyExists) {
			await Swal.fire({
				icon: 'error',
				title: 'Section already exists',
				text: nextSectionName + ' already exists in this release.',
			});
			return;
		}

		const newItem = this.createEmptyItem();
		this.editorPayload.sections = this.sortSections([
			...this.editorPayload.sections,
			{
				sectionName: nextSectionName,
				items: [newItem],
			},
		]);
		this.openDraftItemForEditing(newItem.id);
		this.collapsedSectionIndices = new Set<number>();
		this.isDirty = true;
	}

	public async onSectionNameChanged(section: GroupedChangelogSectionDto, nextSectionName: string): Promise<void> {
		if (this.editorPayload == null || section.sectionName === nextSectionName) {
			return;
		}

		const alreadyExists = this.editorPayload.sections.some(existingSection => existingSection !== section && existingSection.sectionName === nextSectionName);
		if (alreadyExists) {
			await Swal.fire({
				icon: 'error',
				title: 'Section already exists',
				text: nextSectionName + ' already exists in this release.',
			});
			this.changeDetectorRef.markForCheck();
			return;
		}

		section.sectionName = nextSectionName;
		this.editorPayload.sections = this.sortSections(this.editorPayload.sections);
		this.collapsedSectionIndices = new Set<number>();
		this.markDirty();
	}
	public async removeSection(sectionIndex: number): Promise<void> {
		if (this.editorPayload == null) {
			return;
		}

		const section = this.editorPayload.sections[sectionIndex];
		if (section == null) {
			return;
		}

		const removedSection = this.cloneDraftSection(section);
		const previousCollapsedSectionIndices = new Set(this.collapsedSectionIndices);
		const previousSelectedBulkItemIds = [...this.selectedBulkItemIds];
		const previousExpandedDraftItemIds = new Set(this.expandedDraftItemIds);
		const wasDirty = this.isDirty;

		const result = await Swal.fire({
			icon: 'warning',
			title: 'Remove section?',
			text: `Remove the ${section.sectionName} section and its ${section.items.length} ${section.items.length === 1 ? 'point' : 'points'} from this draft?`,
			showCancelButton: true,
			confirmButtonText: 'Remove section',
			cancelButtonText: 'Keep section',
		});

		if (!result.isConfirmed) {
			this.changeDetectorRef.markForCheck();
			return;
		}

		this.editorPayload = {
			...this.editorPayload,
			sections: this.editorPayload.sections.filter((_, index) => index !== sectionIndex),
		};
		this.collapsedSectionIndices = new Set(
			Array.from(this.collapsedSectionIndices)
				.filter(index => index !== sectionIndex)
				.map(index => index > sectionIndex ? index - 1 : index),
		);
		this.removeOrphanedBulkSelections();
		this.isDirty = true;
		this.changeDetectorRef.markForCheck();

		const undoResult = await this.offerUndoRemoval('Section removed', `${section.sectionName} was removed from the draft.`);
		if (!undoResult.isConfirmed || this.editorPayload == null) {
			this.changeDetectorRef.markForCheck();
			return;
		}

		const restoredSections = [...this.editorPayload.sections];
		restoredSections.splice(sectionIndex, 0, removedSection);
		this.editorPayload = {
			...this.editorPayload,
			sections: restoredSections,
		};
		this.collapsedSectionIndices = previousCollapsedSectionIndices;
		this.selectedBulkItemIds = previousSelectedBulkItemIds;
		this.expandedDraftItemIds.clear();
		for (const itemId of previousExpandedDraftItemIds) {
			this.expandedDraftItemIds.add(itemId);
		}
		this.isDirty = wasDirty;
		this.changeDetectorRef.markForCheck();
	}

	public addItem(section: GroupedChangelogSectionDto): void {
		const newItem = this.createEmptyItem();
		section.items.push(newItem);
		this.openDraftItemForEditing(newItem.id);
		this.isDirty = true;
	}

	public async removeItem(section: GroupedChangelogSectionDto, itemIndex: number): Promise<void> {
		if (this.editorPayload == null) {
			return;
		}

		const item = section.items[itemIndex];
		if (item == null) {
			return;
		}

		const removedItem = this.cloneDraftItem(item);
		const wasItemSelected = this.selectedBulkItemIds.includes(item.id);
		const wasItemExpanded = this.expandedDraftItemIds.has(item.id);
		const wasDirty = this.isDirty;

		const result = await Swal.fire({
			icon: 'warning',
			title: 'Remove point?',
			text: `Remove point ${itemIndex + 1} from the ${section.sectionName} section?`,
			showCancelButton: true,
			confirmButtonText: 'Remove point',
			cancelButtonText: 'Keep point',
		});

		if (!result.isConfirmed) {
			this.changeDetectorRef.markForCheck();
			return;
		}

		const sectionIndex = this.editorPayload.sections.indexOf(section);
		if (sectionIndex < 0) {
			this.changeDetectorRef.markForCheck();
			return;
		}

		const nextItems = section.items.filter((_, index) => index !== itemIndex);
		this.editorPayload = {
			...this.editorPayload,
			sections: this.editorPayload.sections.map((existingSection, index) => index === sectionIndex
				? { ...existingSection, items: nextItems }
				: existingSection),
		};

		if (removedItem != null) {
			this.selectedBulkItemIds = this.selectedBulkItemIds.filter(itemId => itemId !== removedItem.id);
			this.expandedDraftItemIds.delete(removedItem.id);
		}
		this.isDirty = true;
		this.changeDetectorRef.markForCheck();

		const undoResult = await this.offerUndoRemoval('Point removed', `Point ${itemIndex + 1} was removed from ${section.sectionName}.`);
		if (!undoResult.isConfirmed || this.editorPayload == null) {
			this.changeDetectorRef.markForCheck();
			return;
		}

		this.editorPayload = {
			...this.editorPayload,
			sections: this.editorPayload.sections.map((existingSection, index) => {
				if (index !== sectionIndex) {
					return existingSection;
				}

				const restoredItems = [...existingSection.items];
				restoredItems.splice(itemIndex, 0, removedItem);
				return { ...existingSection, items: restoredItems };
			}),
		};

		if (wasItemSelected) {
			this.selectedBulkItemIds = [...this.selectedBulkItemIds, removedItem.id];
		}

		if (wasItemExpanded) {
			this.expandedDraftItemIds.add(removedItem.id);
		}

		this.isDirty = wasDirty;
		this.changeDetectorRef.markForCheck();
	}

	public get draftSectionDropListIds(): string[] {
		return this.editorPayload?.sections.map((_, index) => this.getDraftSectionDropListId(index)) ?? [];
	}

	public onDraftItemDragStart(itemId: string): void {
		this.draggingDraftItemId = itemId;
	}

	public onDraftItemDragEnd(): void {
		this.clearDraftDragState();
	}

	public onDraftItemDrop(event: CdkDragDrop<GroupedChangelogItemDto[]>): void {
		if (this.editorPayload == null) {
			return;
		}

		if (event.previousContainer === event.container) {
			if (event.previousIndex === event.currentIndex) {
				this.clearDraftDragState();
				return;
			}

			moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
		} else {
			transferArrayItem(
				event.previousContainer.data,
				event.container.data,
				event.previousIndex,
				event.currentIndex,
			);
		}

		this.markDirty();
		this.clearDraftDragState();
	}

	private clearDraftDragState(): void {
		this.draggingDraftItemId = undefined;
	}

	public toggleDraftItemsCompact(): void {
		this.isDraftItemsCompact = !this.isDraftItemsCompact;
		this.expandedDraftItemIds.clear();
		this.changeDetectorRef.markForCheck();
	}

	public toggleDraftItemExpanded(itemId: string): void {
		if (!this.isDraftItemsCompact) {
			return;
		}

		if (this.expandedDraftItemIds.has(itemId)) {
			this.expandedDraftItemIds.delete(itemId);
		} else {
			this.expandedDraftItemIds.add(itemId);
		}

		this.changeDetectorRef.markForCheck();
	}

	public isDraftItemExpanded(itemId: string): boolean {
		return !this.isDraftItemsCompact || this.expandedDraftItemIds.has(itemId);
	}

	public getDraftItemCompactText(item: GroupedChangelogItemDto): string {
		return item.text.trim() || 'No user-facing text yet.';
	}

	private openDraftItemForEditing(itemId: string): void {
		if (!this.isDraftItemsCompact) {
			return;
		}

		this.expandedDraftItemIds.add(itemId);
	}

	public toggleBulkSelection(itemId: string, selected: boolean): void {
		const currentIds = new Set(this.selectedBulkItemIds);
		if (selected) {
			currentIds.add(itemId);
		} else {
			currentIds.delete(itemId);
		}

		this.selectedBulkItemIds = Array.from(currentIds);
	}

	public async applyBulkUpdate(): Promise<void> {
		if (!this.canApplyBulkUpdate || this.selectedOverview == null) {
			return;
		}

		this.applyingBulkUpdate = true;
		this.changeDetectorRef.markForCheck();

		try {
			const request: BulkUpdateChangelogReleaseDto = {
				itemIds: [...this.selectedBulkItemIds],
				sectionName: this.bulkSectionName || undefined,
				visibility: this.bulkVisibility || undefined,
				scope: this.bulkScope || undefined,
				targetApplicationKeys: this.bulkTargetApplicationKeys.length > 0 ? [...this.bulkTargetApplicationKeys] : undefined,
			};

			const release = await firstValueFrom(this.changelogService.bulkUpdateRelease(
				this.selectedOverview.releaseFamilyKey,
				this.selectedOverview.version,
				this.selectedOverview.isPreRelease,
				request,
			));
			this.applyReleaseResponse(release);
			this.selectedBulkItemIds = [];
			this.bulkSectionName = '';
			this.bulkVisibility = '';
			this.bulkScope = '';
			this.bulkTargetApplicationKeys = [];
			await this.refreshPreview();
		} catch (error) {
			showErrorSwal(error);
		} finally {
			this.applyingBulkUpdate = false;
			this.changeDetectorRef.markForCheck();
		}
	}

	public markDirty(): void {
		this.isDirty = true;
	}

	public onReleaseDateChanged(value: Date | string | null | undefined): void {
		if (this.editorPayload == null) {
			return;
		}

		const previousTitle = this.editorPayload.title?.trim() ?? '';
		const previousAutoTitle = this.getDefaultReleaseTitle(this.editorPayload.releaseDate);
		const nextReleaseDate = this.toIsoDateString(value);
		this.editorPayload.releaseDate = nextReleaseDate;

		if (!previousTitle || previousTitle === previousAutoTitle) {
			this.editorPayload.title = this.getDefaultReleaseTitle(nextReleaseDate);
		}

		this.markDirty();
	}

	public onBulkScopeChanged(): void {
		if (this.bulkScope !== 'Application') {
			this.bulkTargetApplicationKeys = [];
		}
	}

	public openItemApplicationSelector(item: GroupedChangelogItemDto): void {
		this.itemApplicationSelectorItem = item;
		this.itemApplicationSelectorMode = 'editor';
		this.draftItemApplicationKeys = [...item.targetApplicationKeys];
		this.itemApplicationSearch = '';
	}

	public openWorkflowItemApplicationSelector(item: SourceFileWorkflowItem): void {
		this.itemApplicationSelectorItem = item;
		this.itemApplicationSelectorMode = 'workflow';
		this.draftItemApplicationKeys = [...item.targetApplicationKeys];
		this.itemApplicationSearch = '';
	}

	public closeItemApplicationSelector(): void {
		this.itemApplicationSelectorItem = undefined;
		this.itemApplicationSelectorMode = null;
		this.draftItemApplicationKeys = [];
		this.itemApplicationSearch = '';
	}

	public cancelItemApplicationSelector(): void {
		this.closeItemApplicationSelector();
	}

	public clearItemApplicationDraft(): void {
		this.draftItemApplicationKeys = [];
	}

	public applyItemApplicationSelector(): void {
		if (this.itemApplicationSelectorItem == null) {
			return;
		}

		const nextKeys = [...this.draftItemApplicationKeys];
		if (!this.areSelectionKeysEqual(nextKeys, this.itemApplicationSelectorItem.targetApplicationKeys)) {
			this.itemApplicationSelectorItem.targetApplicationKeys = nextKeys;
			this.itemApplicationSelectorItem.scope = nextKeys.length > 0 ? 'Application' : 'Global';
			if (this.itemApplicationSelectorMode === 'editor') {
				this.markDirty();
			}
		}

		this.closeItemApplicationSelector();
	}

	public toggleItemApplicationDraft(applicationKey: string, selected: boolean): void {
		const nextSelectedKeys = new Set(this.draftItemApplicationKeys);
		if (selected) {
			nextSelectedKeys.add(applicationKey);
		} else {
			nextSelectedKeys.delete(applicationKey);
		}

		this.draftItemApplicationKeys = this.availableApplications
			.map(application => application.applicationKey)
			.filter(key => nextSelectedKeys.has(key));
	}

	public isDraftItemApplicationSelected(applicationKey: string): boolean {
		return this.draftItemApplicationKeys.includes(applicationKey);
	}

	public setBulkTargetApplicationKeys(event: Event): void {
		this.bulkTargetApplicationKeys = this.getSelectedValues(event);
	}

	public setItemTargetApplicationKeys(item: GroupedChangelogItemDto, event: Event): void {
		item.targetApplicationKeys = this.getSelectedValues(event);
		item.scope = item.targetApplicationKeys.length > 0 ? 'Application' : 'Global';
		this.markDirty();
	}

	public toggleSourceSelection(sourceId: number, selected: boolean): void {
		const nextSelectedIds = new Set(this.selectedSourceIds);
		if (selected) {
			nextSelectedIds.add(sourceId);
		} else {
			nextSelectedIds.delete(sourceId);
		}

		this.selectedSourceIds = Array.from(nextSelectedIds).sort((left, right) => left - right);
		this.markDirty();
	}

	public selectAllAvailableSources(): void {
		this.selectedSourceIds = (this.release?.availableSources ?? []).map(source => source.id);
		this.markDirty();
	}

	public resetSourceSelectionToSaved(): void {
		this.selectedSourceIds = (this.release?.sources ?? []).map(source => source.id);
		this.markDirty();
	}

	public selectSourcesMatchingVersion(version: string): void {
		this.selectedSourceIds = (this.release?.availableSources ?? [])
			.filter(source => source.version === version)
			.map(source => source.id);
		this.markDirty();
	}

	public isSelectedSource(sourceId: number): boolean {
		return this.selectedSourceIds.includes(sourceId);
	}

	public isSavedSource(sourceId: number): boolean {
		return (this.release?.sources ?? []).some(source => source.id === sourceId);
	}

	public isBulkSelected(itemId: string): boolean {
		return this.selectedBulkItemIds.includes(itemId);
	}

	public isBulkTargetApplicationSelected(applicationKey: string): boolean {
		return this.bulkTargetApplicationKeys.includes(applicationKey);
	}

	public isItemTargetApplicationSelected(item: GroupedChangelogItemDto, applicationKey: string): boolean {
		return item.targetApplicationKeys.includes(applicationKey);
	}

	public getItemTargetApplicationDisplayNames(item: { targetApplicationKeys: string[] }): string[] {
		if (item.targetApplicationKeys.length === 0) {
			return [];
		}

		const displayNameByKey = new Map(
			this.availableApplications.map(application => [application.applicationKey, application.displayName]),
		);

		return item.targetApplicationKeys.map(applicationKey => displayNameByKey.get(applicationKey) ?? applicationKey);
	}

	public getApplicationSelectionSummaryText(item: { targetApplicationKeys: string[] }): string {
		const displayNames = this.getItemTargetApplicationDisplayNames(item);
		if (displayNames.length === 0) {
			return 'All applications';
		}

		return displayNames.join(', ');
	}

	public getLinkedSourceSummary(item: GroupedChangelogItemDto): string {
		const linkedChangeCount = item.sourceReferences.length;
		if (linkedChangeCount === 0) {
			return item.isManuallyEdited ? 'Manual point' : 'No linked changes';
		}

		const sourceCount = new Set(item.sourceReferences.map(reference => reference.sourceChangelogId)).size;
		const changeText = linkedChangeCount === 1 ? '1 linked change' : `${linkedChangeCount} linked changes`;
		const sourceText = sourceCount === 1 ? '1 raw changelog' : `${sourceCount} raw changelogs`;
		return `${changeText} from ${sourceText}`;
	}

	public getResolvedSourceReferences(item: GroupedChangelogItemDto): ResolvedSourceReferenceViewModel[] {
		return item.sourceReferences.map(reference => this.toResolvedSourceReference(reference));
	}

	public getWorkflowSourceCardHeading(group: SourceFileWorkflowGroup): string {
		return this.getWorkflowSourceTitle(group) ?? group.fileName;
	}

	public getWorkflowReviewHeading(group: SourceFileWorkflowGroup): string {
		return this.getWorkflowSourceTitle(group) ?? group.fileName;
	}

	public getWorkflowReviewSelectedPointCount(group: SourceFileWorkflowGroup): number {
		return group.items.filter(item => item.included).length;
	}

	public getWorkflowReferenceSummary(item: SourceFileWorkflowItem): string {
		const labels = item.sourceReferences.map(reference => `${reference.rawSectionName} #${reference.rawSortOrder}`);
		const distinctLabels = labels.filter((label, index) => labels.indexOf(label) === index);
		if (distinctLabels.length === 0) {
			return 'No raw references';
		}

		if (distinctLabels.length <= 3) {
			return distinctLabels.join(' · ');
		}

		return `${distinctLabels.slice(0, 3).join(' · ')} +${distinctLabels.length - 3} more`;
	}

	public getWorkflowReviewMarkdown(group: SourceFileWorkflowGroup): string | null {
		return this.getSourceFragmentForGroup(group)?.markdown?.trim() || null;
	}

	public isWorkflowReviewMarkdownLoading(group: SourceFileWorkflowGroup): boolean {
		return this.loadingSourceDetailIds.has(group.sourceChangelogId);
	}

	public getWorkflowReviewMarkdownStatusText(group: SourceFileWorkflowGroup): string {
		if (this.isWorkflowReviewMarkdownLoading(group)) {
			return 'Loading source markdown...';
		}

		if (this.sourceDetailLoadFailures.has(group.sourceChangelogId)) {
			return 'The source markdown could not be loaded for this file.';
		}

		return 'No source markdown was found for this file.';
	}

	public openWorkflowModal(): void {
		this.initializeSourceFileSelection();
		this.workflowReviewFileIndex = 0;
		this.workflowModalStep = 'select';
		this.isWorkflowModalOpen = true;
		this.changeDetectorRef.markForCheck();
	}

	public closeWorkflowModal(): void {
		this.isWorkflowModalOpen = false;
		this.changeDetectorRef.markForCheck();
	}

	public isSourceFileSelected(sourceFileKey: string): boolean {
		return this.selectedSourceFileKeys.includes(sourceFileKey);
	}

	public toggleSourceFileSelection(sourceFileKey: string, selected: boolean): void {
		const nextKeys = new Set(this.selectedSourceFileKeys);
		if (selected) {
			nextKeys.add(sourceFileKey);
		} else {
			nextKeys.delete(sourceFileKey);
		}

		this.selectedSourceFileKeys = this.sourceFileGroups
			.map(group => group.key)
			.filter(groupKey => nextKeys.has(groupKey));
	}

	public selectAllSourceFiles(): void {
		this.selectedSourceFileKeys = this.sourceFileGroups.map(group => group.key);
	}

	public clearSourceFileSelection(): void {
		this.selectedSourceFileKeys = [];
	}

	public startWorkflowReview(): void {
		if (!this.canStartWorkflowReview) {
			return;
		}

		this.workflowReviewFileIndex = 0;
		this.workflowModalStep = 'review';
	}

	public goToPreviousWorkflowReview(): void {
		if (this.workflowReviewFileIndex <= 0) {
			return;
		}

		this.workflowReviewFileIndex--;
	}

	public goToNextWorkflowReview(): void {
		if (this.workflowReviewFileIndex >= this.workflowSelectedSourceFileGroups.length - 1) {
			return;
		}

		this.workflowReviewFileIndex++;
	}

	public includeAllItemsForCurrentWorkflowFile(): void {
		const group = this.currentWorkflowReviewGroup;
		if (group == null) {
			return;
		}

		group.items.forEach(item => item.included = true);
	}

	public clearAllItemsForCurrentWorkflowFile(): void {
		const group = this.currentWorkflowReviewGroup;
		if (group == null) {
			return;
		}

		group.items.forEach(item => item.included = false);
	}

	public openWorkflowConfirm(): void {
		this.workflowModalStep = 'confirm';
	}

	public backToWorkflowSelection(): void {
		this.workflowModalStep = 'select';
	}

	public backToWorkflowReview(): void {
		this.workflowModalStep = 'review';
	}

	public applyWorkflowToMainEditor(): void {
		if (this.release == null || this.editorPayload == null) {
			return;
		}

		const nextPayload = this.clonePayload(this.release.suggestedPayload);
		nextPayload.version = this.editorPayload.version;
		nextPayload.releaseDate = this.editorPayload.releaseDate;
		nextPayload.title = this.editorPayload.title;
		nextPayload.summary = this.editorPayload.summary;
		nextPayload.sourceApplicationKeys = this.getSourceApplicationKeysForSelectedFiles(new Set(this.selectedSourceFileKeys));
		nextPayload.sections = this.workflowConfirmSections.length > 0
			? this.workflowConfirmSections.map(section => ({
				sectionName: section.sectionName,
				items: section.items.map(item => ({
					id: item.id,
					text: item.text,
					visibility: item.visibility,
					scope: item.scope,
					targetApplicationKeys: [...item.targetApplicationKeys],
					isManuallyEdited: item.isManuallyEdited,
					sourceReferences: item.sourceReferences.map(reference => ({ ...reference })),
				})),
			}))
			: [{ sectionName: 'Notes', items: [] }];

		this.editorPayload = nextPayload;
		this.collapsedSectionIndices = new Set<number>();
		this.selectedBulkItemIds = [];
		this.refreshWorkflowDraftingState();
		this.isDirty = true;
		this.closeWorkflowModal();
		this.changeDetectorRef.markForCheck();
		void this.preloadReferencedSourceDetails();
	}

	public rebuildDraftFromSelectedSourceFiles(): void {
		if (this.release == null || this.editorPayload == null) {
			return;
		}

		const selectedKeys = [...this.selectedSourceFileKeys].sort();
		const currentDraftKeys = this.getSourceFileSelectionKeysFromPayload(this.editorPayload).sort();
		const selectionChanged = !this.areSelectionKeysEqual(selectedKeys, currentDraftKeys);
		if (this.totalDraftItems > 0 && (this.isDirty || selectionChanged) && !this.confirmRebuildFromSourceFiles()) {
			return;
		}

		const selectedKeySet = new Set(selectedKeys);
		const rebuiltSections = this.sortSections(this.release.suggestedPayload.sections
			.map(section => ({
				sectionName: section.sectionName,
				items: section.items
					.map(item => {
						const sourceReferences = item.sourceReferences.filter(reference => selectedKeySet.has(this.getSourceFileSelectionKey(reference)));
						if (sourceReferences.length === 0) {
							return null;
						}

						return {
							...item,
							targetApplicationKeys: [...item.targetApplicationKeys],
							sourceReferences,
						} as GroupedChangelogItemDto;
					})
					.filter((item): item is GroupedChangelogItemDto => item != null),
			}))
			.filter(section => section.items.length > 0));
		const nextPayload = this.clonePayload(this.release.suggestedPayload);
		nextPayload.version = this.editorPayload.version;
		nextPayload.releaseDate = this.editorPayload.releaseDate;
		nextPayload.title = this.editorPayload.title;
		nextPayload.summary = this.editorPayload.summary;
		nextPayload.sourceApplicationKeys = this.getSourceApplicationKeysForSelectedFiles(selectedKeySet);
		nextPayload.sections = rebuiltSections.length > 0
			? rebuiltSections
			: [{ sectionName: 'Notes', items: [] }];

		this.editorPayload = nextPayload;
		this.collapsedSectionIndices = new Set<number>();
		this.selectedBulkItemIds = [];
		this.refreshWorkflowDraftingState();
		this.isDirty = true;
		this.changeDetectorRef.markForCheck();
		void this.preloadReferencedSourceDetails();
	}


	public toggleSectionExpanded(sectionIndex: number): void {
		if (this.collapsedSectionIndices.has(sectionIndex)) {
			this.collapsedSectionIndices.delete(sectionIndex);
		} else {
			this.collapsedSectionIndices.add(sectionIndex);
		}

		this.changeDetectorRef.markForCheck();
	}

	public isSectionExpanded(sectionIndex: number): boolean {
		return !this.collapsedSectionIndices.has(sectionIndex);
	}

	public getSectionToneClass(sectionName: string): string {
		switch (sectionName) {
			case 'Added':
				return 'draft-section--added';
			case 'Changed':
				return 'draft-section--changed';
			case 'Fixed':
				return 'draft-section--fixed';
			case 'Removed':
				return 'draft-section--removed';
			case 'Deprecated':
				return 'draft-section--deprecated';
			case 'Security':
				return 'draft-section--security';
			default:
				return 'draft-section--notes';
		}
	}

	public trackBySectionIndex(index: number): number {
		return index;
	}

	public getDraftSectionDropListId(sectionIndex: number): string {
		return `draft-section-${sectionIndex}`;
	}

	public trackByItemId(_: number, item: GroupedChangelogItemDto): string {
		return item.id;
	}

	public trackBySourceReferenceKey(_: number, reference: ResolvedSourceReferenceViewModel): string {
		return reference.key;
	}

	public trackBySourceFileKey(_: number, group: SourceFileWorkflowGroup): string {
		return group.key;
	}

	public trackBySourceId(_: number, source: ChangelogReleaseSourceDto): number {
		return source.id;
	}

	public trackByApplicationKey(_: number, application: ChangelogTargetApplicationOptionDto): string {
		return application.applicationKey;
	}

	public trackBySelectionKey(_: number, selection: ReleaseSelectionSummary): string {
		return selection.selectionKey;
	}

	public formatSectionOverview(sections: { sectionName: string; itemCount: number }[]): string {
		return this.sortSections(sections)
			.map(section => section.sectionName + ' (' + section.itemCount + ')')
			.join(', ');
	}

	private async loadSingleRelease(selection: ReleaseSelectionSummary, openWorkflowModal = false): Promise<boolean> {
		this.loadingEntry = true;
		this.changeDetectorRef.markForCheck();

		try {
			const release = await firstValueFrom(this.changelogService.getRelease(
				selection.releaseFamilyKey,
				selection.version,
				selection.isPreRelease,
			));

			this.selectedOverview = selection;
			this.applyReleaseResponse(release, openWorkflowModal);
			this.previewAudience = this.allowedPreviewAudiences[0] ?? 'Users';
			this.previewStage = release.publishedPayload != null ? 'Published' : 'Draft';
			this.isSelectingRelease = false;

			await this.refreshPreview();
			return true;
		} catch (error) {
			showErrorSwal(error);
			return false;
		} finally {
			this.loadingEntry = false;
			this.changeDetectorRef.markForCheck();
		}
	}


	private async loadMergedRelease(selections: ReleaseSelectionSummary[], openWorkflowModal = false): Promise<boolean> {
		this.loadingEntry = true;
		this.changeDetectorRef.markForCheck();

		try {
			const mergedRelease = await firstValueFrom(this.changelogService.getMergedRelease(this.createMergedReleaseRequest(selections)));

			this.selectedOverview = selections[0];
			this.applyReleaseResponse(mergedRelease, openWorkflowModal);
			this.previewAudience = this.allowedPreviewAudiences[0] ?? 'Users';
			this.previewStage = mergedRelease.publishedPayload != null ? 'Published' : 'Draft';
			this.isSelectingRelease = false;

			await this.refreshPreview();
			return true;
		} catch (error) {
			showErrorSwal(error);
			return false;
		} finally {
			this.loadingEntry = false;
			this.changeDetectorRef.markForCheck();
		}
	}

	private async saveRelease(status: string): Promise<void> {
		if (this.selectedOverview == null || this.editorPayload == null) {
			return;
		}

		this.saving = true;
		this.changeDetectorRef.markForCheck();

		try {
			const release = await firstValueFrom(this.changelogService.saveRelease(
				this.selectedOverview.releaseFamilyKey,
				this.selectedOverview.version,
				this.selectedOverview.isPreRelease,
				{
					status,
					draftPayload: this.clonePayload(this.editorPayload),
					publishedPayload: status === 'Published' ? this.clonePayload(this.editorPayload) : undefined,
					sourceChangelogIds: [...this.selectedSourceIds],
				},
			));

			this.applyReleaseResponse(release);
			this.previewStage = release.publishedPayload != null ? 'Published' : 'Draft';
			await this.loadOverview(this.confirmedOverviewSelections.map(selection => selection.selectionKey));
			await this.refreshPreview();
			this.isReleaseBrowserVisible = false;
		} catch (error) {
			showErrorSwal(error);
		} finally {
			this.saving = false;
			this.changeDetectorRef.markForCheck();
		}
	}


	private applyReleaseResponse(release: ChangelogReleaseEditorDto, openWorkflowModal = false): void {
		this.release = release;
		this.editorPayload = this.normalizeEditorPayload(
			this.clonePayload(release.draftPayload),
			release.suggestedPayload,
		);
		this.collapsedSectionIndices = new Set<number>();
		this.expandedDraftItemIds.clear();
		this.isDirty = false;
		this.selectedBulkItemIds = [];
		this.selectedSourceIds = release.sources.map(source => source.id);
		this.closeItemApplicationSelector();
		this.bulkSectionName = '';
		this.bulkVisibility = '';
		this.bulkScope = '';
		this.bulkTargetApplicationKeys = [];
		this.removeOrphanedBulkSelections();
		this.initializeSourceFileSelection();
		this.refreshWorkflowDraftingState();
		this.isWorkflowModalOpen = openWorkflowModal && !this.hasCompletedWorkflowDrafting;
		this.resetSourceDetailState();
		void this.preloadReferencedSourceDetails();
	}

	private clearSelection(): void {
		this.pendingOverviewSelections = [];
		this.confirmedOverviewSelections = [];
		this.selectedOverview = undefined;
		this.release = undefined;
		this.editorPayload = undefined;
		this.preview = undefined;
		this.isSelectingRelease = false;
		this.selectedRowKeys = [];
		this.selectedBulkItemIds = [];
		this.selectedSourceIds = [];
		this.bulkSectionName = '';
		this.bulkVisibility = '';
		this.bulkScope = '';
		this.bulkTargetApplicationKeys = [];
		this.collapsedSectionIndices = new Set<number>();
		this.expandedDraftItemIds.clear();
		this.closeItemApplicationSelector();
		this.workflowSourceFileGroups = [];
		this.selectedSourceFileKeys = [];
		this.sourceFileSearch = '';
		this.workflowReviewFileIndex = 0;
		this.workflowModalStep = 'select';
		this.hasCompletedWorkflowDrafting = false;
		this.isWorkflowModalOpen = false;
		this.resetSourceDetailState();
		this.isDirty = false;
		this.isReleaseBrowserVisible = true;
	}

	private toOverviewRow(entry: ChangelogReleaseOverviewDto): ChangelogReleaseOverviewRow {
		const isConverted = entry.exists;
		return {
			...entry,
			selectionKey: this.getSelectionKey(entry.releaseFamilyKey, entry.version, entry.isPreRelease),
			displayTitle: `${entry.releaseFamilyName} ${entry.version}`.trim(),
			conversionState: isConverted ? 'Converted' : 'Needs release',
			conversionHint: isConverted ? 'Grouped release already exists' : 'Only raw changelog exports exist',
			workflowStatusText: isConverted ? entry.status : 'Not created',
			sourceApplicationsText: entry.sourceApplicationKeys.join(', '),
			highlightsText: entry.highlights.join(' | '),
		};
	}

	private toSelectionSummary(row: ChangelogReleaseOverviewRow): ReleaseSelectionSummary {
		return {
			selectionKey: row.selectionKey,
			displayTitle: row.displayTitle,
			releaseFamilyKey: row.releaseFamilyKey,
			version: row.version,
			isPreRelease: row.isPreRelease,
			sourceCount: row.sourceCount,
		};
	}

	private clonePayload(payload: GroupedChangelogPayloadDto): GroupedChangelogPayloadDto {
		const clonedPayload = JSON.parse(JSON.stringify(payload)) as GroupedChangelogPayloadDto;
		clonedPayload.sections = this.sortSections(clonedPayload.sections ?? []);
		return clonedPayload;
	}

	private cloneDraftSection(section: GroupedChangelogSectionDto): GroupedChangelogSectionDto {
		return {
			sectionName: section.sectionName,
			items: section.items.map(item => this.cloneDraftItem(item)),
		};
	}

	private cloneDraftItem(item: GroupedChangelogItemDto): GroupedChangelogItemDto {
		return {
			...item,
			targetApplicationKeys: [...item.targetApplicationKeys],
			sourceReferences: item.sourceReferences.map(reference => ({ ...reference })),
		};
	}

	private normalizeEditorPayload(
		payload: GroupedChangelogPayloadDto,
		suggestedPayload?: GroupedChangelogPayloadDto,
	): GroupedChangelogPayloadDto {
		const normalizedReleaseDate = payload.releaseDate?.trim() || this.toIsoDateString(new Date());
		const trimmedTitle = payload.title?.trim() ?? '';
		const suggestedTitle = suggestedPayload?.title?.trim() ?? '';
		const defaultTitleFromExistingDate = this.getDefaultReleaseTitle(payload.releaseDate);
		const defaultTitle = this.getDefaultReleaseTitle(normalizedReleaseDate);

		payload.releaseDate = normalizedReleaseDate;
		payload.summary = this.shouldClearSummary(payload.summary, suggestedPayload?.summary) ? '' : payload.summary?.trim() ?? '';
		payload.title = !trimmedTitle
			|| trimmedTitle === suggestedTitle
			|| trimmedTitle === defaultTitleFromExistingDate
			? defaultTitle
			: trimmedTitle;
		return payload;
	}

	private getBasePreviewPayload(): GroupedChangelogPayloadDto | null {
		if (this.previewStage === 'Published' && this.release?.publishedPayload != null) {
			return this.release.publishedPayload;
		}

		return this.editorPayload ?? this.preview?.payload ?? null;
	}

	private getValidationWarningsForPayload(payload: GroupedChangelogPayloadDto, status: string): ChangelogValidationWarningDto[] {
		const warnings: ChangelogValidationWarningDto[] = [];
		const allItems = payload.sections.reduce((items, section) => {
			items.push(...section.items);
			return items;
		}, [] as GroupedChangelogItemDto[]);
		const totalItems = allItems.length;
		const visibleForUsersCount = allItems.filter(item => item.visibility === 'Users').length;
		const applicationScopedWithoutTargetsCount = allItems.filter(item =>
			item.scope === 'Application' && item.targetApplicationKeys.length === 0,
		).length;

		if (!payload.summary?.trim()) {
			warnings.push({
				code: 'MissingSummary',
				message: 'Add a short summary before publishing.',
			});
		}

		if (totalItems === 0) {
			warnings.push({
				code: 'EmptyChangelog',
				message: 'No grouped changelog items are present.',
			});
		}

		if (visibleForUsersCount === 0) {
			warnings.push({
				code: 'NoUserVisibleItems',
				message: 'Nothing is currently visible to users.',
			});
		}

		if (applicationScopedWithoutTargetsCount > 0) {
			warnings.push({
				code: 'ApplicationItemsMissingTargets',
				message: 'One or more application-scoped items have no target applications selected.',
			});
		}

		const duplicateTextMap = allItems
			.filter(item => item.visibility !== 'Hide')
			.map(item => item.text.trim())
			.filter(text => text.length > 0)
			.reduce((counts, text) => {
				const key = text.toLocaleLowerCase();
				const existing = counts.get(key);
				if (existing == null) {
					counts.set(key, { text, count: 1 });
					return counts;
				}

				counts.set(key, { text: existing.text, count: existing.count + 1 });
				return counts;
			}, new Map<string, { text: string; count: number }>());

		const duplicateDisplayTexts = Array.from(duplicateTextMap.values())
			.filter(entry => entry.count > 1)
			.map(entry => entry.text)
			.slice(0, 3);

		if (duplicateDisplayTexts.length > 0) {
			warnings.push({
				code: 'DuplicateItems',
				message: `Duplicate visible entries detected: ${duplicateDisplayTexts.join(' | ')}`,
			});
		}

		if (status === 'Published' && visibleForUsersCount === 0) {
			warnings.push({
				code: 'PublishedWithoutUserItems',
				message: 'This release is marked as published but has no user-visible items.',
			});
		}

		return warnings;
	}

	private createFilteredPreviewPayload(payload: GroupedChangelogPayloadDto): GroupedChangelogPayloadDto {
		return {
			...payload,
			sourceApplicationKeys: [...payload.sourceApplicationKeys],
			sections: payload.sections
				.map(section => ({
					sectionName: section.sectionName,
					items: section.items
						.filter(item => this.shouldIncludePreviewItem(item))
						.map(item => ({
							...item,
							targetApplicationKeys: [...item.targetApplicationKeys],
							sourceReferences: item.sourceReferences.map(reference => ({ ...reference })),
						})),
				}))
				.filter(section => section.items.length > 0),
		};
	}

	private shouldIncludePreviewItem(item: GroupedChangelogItemDto): boolean {
		if (this.previewAudience === 'Users' && item.visibility !== 'Users') {
			return false;
		}

		return true;
	}

	private shouldClearSummary(summary?: string | null, suggestedSummary?: string | null): boolean {
		const trimmedSummary = summary?.trim() ?? '';
		if (!trimmedSummary) {
			return true;
		}

		return trimmedSummary === (suggestedSummary?.trim() ?? '');
	}

	private getPayloadDisplayTitle(payload?: GroupedChangelogPayloadDto | null): string {
		if (payload == null) {
			return '';
		}

		const title = payload.title?.trim();
		return title || this.getDefaultReleaseTitle(payload.releaseDate);
	}

	private offerUndoRemoval(title: string, text: string) {
		return Swal.fire({
			toast: true,
			position: 'bottom-end',
			backdrop: false,
			icon: 'info',
			title,
			text,
			showConfirmButton: true,
			confirmButtonText: 'Undo',
			showCloseButton: true,
			timer: 5000,
			timerProgressBar: true,
		});
	}

	private getDefaultReleaseTitle(releaseDate?: string | null): string {
		return `Release (${releaseDate?.trim() || this.toIsoDateString(new Date())})`;
	}

	private parseReleaseDate(value?: string | null): Date | null {
		const trimmedValue = value?.trim();
		if (!trimmedValue) {
			return null;
		}

		const match = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
		if (match != null) {
			const [, year, month, day] = match;
			return new Date(Number(year), Number(month) - 1, Number(day));
		}

		const parsedValue = new Date(trimmedValue);
		return Number.isNaN(parsedValue.getTime()) ? null : parsedValue;
	}

	private toIsoDateString(value: Date | string | null | undefined): string {
		const dateValue = value instanceof Date
			? value
			: typeof value === 'string'
				? this.parseReleaseDate(value) ?? new Date(value)
				: new Date();

		if (Number.isNaN(dateValue.getTime())) {
			return new Date().toISOString().slice(0, 10);
		}

		const year = dateValue.getFullYear();
		const month = String(dateValue.getMonth() + 1).padStart(2, '0');
		const day = String(dateValue.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}

	private sortSections<T extends { sectionName: string }>(sections: T[]): T[] {
		const sectionOrder = new Map(this.allowedSections.map((sectionName, index) => [sectionName, index] as const));

		return [...sections].sort((left, right) => {
			const leftOrder = sectionOrder.get(left.sectionName) ?? Number.MAX_SAFE_INTEGER;
			const rightOrder = sectionOrder.get(right.sectionName) ?? Number.MAX_SAFE_INTEGER;
			if (leftOrder !== rightOrder) {
				return leftOrder - rightOrder;
			}

			return left.sectionName.localeCompare(right.sectionName);
		});
	}

	private createMergedReleaseRequest(selections: ReleaseSelectionSummary[]): MergeChangelogReleaseRequestDto {
		return {
			selections: selections.map(selection => ({
				releaseFamilyKey: selection.releaseFamilyKey,
				version: selection.version,
				isPreRelease: selection.isPreRelease,
			})),
		};
	}

	private createMergedReleasePreviewRequest(selections: ReleaseSelectionSummary[]): MergeChangelogReleasePreviewRequestDto {
		return {
			selections: this.createMergedReleaseRequest(selections).selections,
			audience: this.previewAudience,
			stage: this.previewStage,
		};
	}

	private createEmptyItem(): GroupedChangelogItemDto {
		return {
			id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
			text: '',
			visibility: 'Users',
			scope: 'Global',
			targetApplicationKeys: [],
			isManuallyEdited: true,
			sourceReferences: [],
		};
	}

	private initializeSourceFileSelection(): void {
		this.workflowSourceFileGroups = this.buildSourceFileGroups();
		const allKeys = this.getSourceFileSelectionKeysFromPayload(this.release?.suggestedPayload);
		const draftKeys = this.getSourceFileSelectionKeysFromPayload(this.editorPayload);
		const draftKeySet = new Set(draftKeys);

		this.selectedSourceFileKeys = draftKeys.length > 0
			? allKeys.filter(key => draftKeySet.has(key))
			: [...allKeys];
		this.sourceFileSearch = '';
		this.workflowReviewFileIndex = 0;
		this.workflowModalStep = 'select';
	}

	private refreshWorkflowDraftingState(): void {
		this.hasCompletedWorkflowDrafting = this.hasWorkflowDraftingBeenCompleted();
	}

	private hasWorkflowDraftingBeenCompleted(): boolean {
		if (this.release == null || this.editorPayload == null) {
			return false;
		}

		if (this.workflowSourceFileGroups.length === 0) {
			return true;
		}

		return this.getWorkflowPayloadSignature(this.editorPayload) !== this.getWorkflowPayloadSignature(this.release.suggestedPayload);
	}

	private getWorkflowPayloadSignature(payload?: GroupedChangelogPayloadDto): string {
		if (payload == null) {
			return '';
		}

		return JSON.stringify(
			payload.sections.map(section => ({
				sectionName: section.sectionName,
				items: section.items.map(item => ({
					id: item.id,
					text: item.text,
					visibility: item.visibility,
					scope: item.scope,
					targetApplicationKeys: [...item.targetApplicationKeys].sort(),
					sourceReferenceKeys: item.sourceReferences
						.map(reference => this.getSourceReferenceKey(reference))
						.sort(),
				})),
			})),
		);
	}

	private resetSourceDetailState(): void {
		this.sourceDetailsById.clear();
		this.loadingSourceDetailIds.clear();
		this.sourceDetailLoadFailures.clear();
	}

	private async preloadReferencedSourceDetails(): Promise<void> {
		if (this.editorPayload == null) {
			return;
		}

		const referencedIds = this.editorPayload.sections.reduce((allSourceIds, section) => {
			for (const item of section.items) {
				for (const reference of item.sourceReferences) {
					allSourceIds.push(reference.sourceChangelogId);
				}
			}

			return allSourceIds;
		}, [] as number[]);
		const sourceIds = Array.from(new Set(referencedIds));
		const missingIds = sourceIds.filter(sourceId =>
			!this.sourceDetailsById.has(sourceId)
			&& !this.loadingSourceDetailIds.has(sourceId)
			&& !this.sourceDetailLoadFailures.has(sourceId),
		);
		if (missingIds.length === 0) {
			return;
		}

		missingIds.forEach(sourceId => this.loadingSourceDetailIds.add(sourceId));
		this.changeDetectorRef.markForCheck();

		try {
			await Promise.all(missingIds.map(async sourceId => {
				try {
					const detail = await firstValueFrom(this.changelogService.getSourceById(sourceId));
					this.sourceDetailsById.set(sourceId, detail);
				} catch {
					this.sourceDetailLoadFailures.add(sourceId);
				} finally {
					this.loadingSourceDetailIds.delete(sourceId);
				}
			}));
		} finally {
			this.changeDetectorRef.markForCheck();
		}
	}

	private toResolvedSourceReference(reference: GroupedChangelogSourceReferenceDto): ResolvedSourceReferenceViewModel {
		const source = this.getSourceMetadata(reference.sourceChangelogId);
		const resolvedItem = this.getSourceReferenceItem(reference);
		const sourceLabel = reference.sourceApplicationName?.trim()
			|| source?.applicationName?.trim()
			|| source?.applicationKey
			|| reference.sourceApplicationKey;

		return {
			key: this.getSourceReferenceKey(reference),
			sourceLabel,
			versionLabel: source?.version ?? '',
			locationLabel: `${reference.rawSectionName} #${reference.rawSortOrder}`,
			description: resolvedItem?.description?.trim() || null,
			sourceFile: reference.sourceFile ?? resolvedItem?.sourceFile ?? null,
			isLoading: this.loadingSourceDetailIds.has(reference.sourceChangelogId),
		};
	}

	private getSourceMetadata(sourceChangelogId: number): ChangelogReleaseSourceDto | undefined {
		if (this.release == null) {
			return undefined;
		}

		return [...this.release.sources, ...this.release.availableSources]
			.find(source => source.id === sourceChangelogId);
	}

	private getSourceReferenceItem(reference: GroupedChangelogSourceReferenceDto): ChangelogItemDto | undefined {
		const detail = this.sourceDetailsById.get(reference.sourceChangelogId);
		if (detail == null) {
			return undefined;
		}

		const matchingSections = detail.sections.filter(section => section.sectionName === reference.rawSectionName);
		for (const section of matchingSections) {
			const exactMatch = section.items.find(item =>
				item.sortOrder === reference.rawSortOrder
				&& (reference.sourceFile == null || item.sourceFile === reference.sourceFile),
			);
			if (exactMatch != null) {
				return exactMatch;
			}

			const fallbackMatch = section.items.find(item => item.sortOrder === reference.rawSortOrder);
			if (fallbackMatch != null) {
				return fallbackMatch;
			}
		}

		return undefined;
	}

	private getSourceReferenceKey(reference: GroupedChangelogSourceReferenceDto): string {
		return [
			reference.sourceChangelogId,
			reference.sourceApplicationKey,
			reference.rawSectionName,
			reference.rawSortOrder,
			reference.sourceFile ?? '',
		].join('::');
	}

	private getWorkflowSourceTitle(group: SourceFileWorkflowGroup): string | null {
		const fragment = this.getSourceFragmentForGroup(group);
		return this.extractShortTitle(fragment?.markdown)
			?? this.extractMondayTitle(fragment?.markdown);
	}

	private getSourceFragmentForGroup(group: SourceFileWorkflowGroup): ChangelogFragmentDto | undefined {
		const detail = this.sourceDetailsById.get(group.sourceChangelogId);
		if (detail == null) {
			return undefined;
		}

		const normalizedSourceFile = this.normalizeSourcePath(group.sourceFile);
		const normalizedFileName = this.normalizeSourcePath(group.fileName);

		return detail.fragments.find(fragment => {
			const normalizedRelativePath = this.normalizeSourcePath(fragment.relativePath);
			const normalizedFragmentFileName = this.normalizeSourcePath(fragment.fileName);

			return normalizedRelativePath === normalizedSourceFile
				|| normalizedFragmentFileName === normalizedFileName;
		});
	}

	private extractMondayTitle(markdown?: string | null): string | null {
		if (!markdown) {
			return null;
		}

		const match = markdown.match(/^###\s+Monday Item\s+\d+\s*:\s*(.+)$/m);
		return match?.[1]?.trim() || null;
	}

	private extractShortTitle(markdown?: string | null): string | null {
		if (!markdown) {
			return null;
		}

		const match = markdown.match(/^Short title:\s*(.+)$/m);
		return match?.[1]?.trim() || null;
	}

	private normalizeSourcePath(value?: string | null): string {
		return (value ?? '')
			.trim()
			.replace(/\\/g, '/')
			.toLocaleLowerCase();
	}

	private buildSourceFileGroups(): SourceFileWorkflowGroup[] {
		if (this.release == null) {
			return [];
		}

		const groups = new Map<string, {
			key: string;
			sourceChangelogId: number;
			sourceApplicationKey: string;
			sourceApplicationName: string;
			versionLabel: string;
			sourceFile: string;
			fileName: string;
			items: SourceFileWorkflowItem[];
			itemIndexById: Map<string, number>;
		}>();

		for (const section of this.release.suggestedPayload.sections) {
			for (const item of section.items) {
				for (const reference of item.sourceReferences) {
					const key = this.getSourceFileSelectionKey(reference);
					const source = this.getSourceMetadata(reference.sourceChangelogId);
					const sourceFile = reference.sourceFile?.trim() || '(No source file)';
					let group = groups.get(key);
					if (group == null) {
						group = {
							key,
							sourceChangelogId: reference.sourceChangelogId,
							sourceApplicationKey: reference.sourceApplicationKey,
							sourceApplicationName: reference.sourceApplicationName?.trim()
								|| source?.applicationName?.trim()
								|| reference.sourceApplicationKey,
							versionLabel: source?.version ?? '',
							sourceFile,
							fileName: this.getDisplayFileName(sourceFile),
							items: [],
							itemIndexById: new Map<string, number>(),
						};
						groups.set(key, group);
					}

					const existingItemIndex = group.itemIndexById.get(item.id);
					if (existingItemIndex == null) {
						group.items.push({
							itemId: `${key}::${item.id}`,
							sectionName: section.sectionName,
							text: item.text,
							originalText: item.text,
							visibility: item.visibility,
							scope: item.scope,
							targetApplicationKeys: [...item.targetApplicationKeys],
							isManuallyEdited: item.isManuallyEdited,
							included: false,
							sourceReferences: [{ ...reference }],
						});
						group.itemIndexById.set(item.id, group.items.length - 1);
					} else {
						group.items[existingItemIndex].sourceReferences.push({ ...reference });
					}
				}
			}
		}

		return Array.from(groups.values())
			.map(group => ({
				key: group.key,
				sourceChangelogId: group.sourceChangelogId,
				sourceApplicationKey: group.sourceApplicationKey,
				sourceApplicationName: group.sourceApplicationName,
				versionLabel: group.versionLabel,
				sourceFile: group.sourceFile,
				fileName: group.fileName,
				pointCount: group.items.length,
				sectionSummary: this.formatSectionOverview(Array.from(group.items
					.reduce((sections, item) => {
						sections.set(item.sectionName, (sections.get(item.sectionName) ?? 0) + 1);
						return sections;
					}, new Map<string, number>()))
					.map(([sectionName, itemCount]) => ({ sectionName, itemCount }))),
				items: group.items,
			}))
			.sort((left, right) =>
				left.sourceApplicationName.localeCompare(right.sourceApplicationName)
				|| left.versionLabel.localeCompare(right.versionLabel)
				|| left.sourceFile.localeCompare(right.sourceFile));
	}

	private getSourceFileSelectionKeysFromPayload(payload?: GroupedChangelogPayloadDto): string[] {
		if (payload == null) {
			return [];
		}

		const keys = new Set<string>();
		for (const section of payload.sections) {
			for (const item of section.items) {
				for (const reference of item.sourceReferences) {
					keys.add(this.getSourceFileSelectionKey(reference));
				}
			}
		}

		return Array.from(keys);
	}

	private getSourceFileSelectionKey(reference: GroupedChangelogSourceReferenceDto): string {
		return `${reference.sourceChangelogId}::${reference.sourceFile?.trim() || '(No source file)'}`;
	}

	private getDisplayFileName(sourceFile: string): string {
		const normalizedPath = sourceFile.replace(/\\/g, '/');
		const pathParts = normalizedPath.split('/');
		return pathParts[pathParts.length - 1] || sourceFile;
	}

	private getSourceApplicationKeysForSelectedFiles(selectedKeySet: Set<string>): string[] {
		return this.sourceFileGroups
			.filter(group => selectedKeySet.has(group.key))
			.map(group => group.sourceApplicationKey)
			.filter((value, index, values) => values.indexOf(value) === index)
			.sort((left, right) => left.localeCompare(right));
	}

	private removeOrphanedBulkSelections(): void {
		if (this.editorPayload == null) {
			this.selectedBulkItemIds = [];
			return;
		}

		const validIds = new Set(
			this.editorPayload.sections.reduce((itemIds, section) => itemIds.concat(section.items.map(item => item.id)), [] as string[]),
		);

		this.selectedBulkItemIds = this.selectedBulkItemIds.filter(itemId => validIds.has(itemId));
	}

	private getSelectionKey(releaseFamilyKey: string, version: string, isPreRelease: boolean): string {
		return `${releaseFamilyKey}::${version}::${isPreRelease}`;
	}

	private getSelectionSummariesByKeys(selectionKeys: string[]): ReleaseSelectionSummary[] {
		const keySet = new Set(selectionKeys);
		return this.overviewRows
			.filter(row => keySet.has(row.selectionKey))
			.map(row => this.toSelectionSummary(row));
	}

	private getLatestSelectionSummaries(): ReleaseSelectionSummary[] {
		if (this.overviewRows.length === 0) {
			return [];
		}

		const rowsWithReleaseDate = this.overviewRows
			.map(row => ({
				row,
				releaseDate: this.parseComparableDate(row.releaseDate),
			}))
			.filter((entry): entry is { row: ChangelogReleaseOverviewRow; releaseDate: string } => entry.releaseDate != null);
		if (rowsWithReleaseDate.length > 0) {
			const latestReleaseDate = rowsWithReleaseDate.reduce(
				(latest, entry) => entry.releaseDate > latest ? entry.releaseDate : latest,
				rowsWithReleaseDate[0].releaseDate,
			);

			return rowsWithReleaseDate
				.filter(entry => entry.releaseDate === latestReleaseDate)
				.map(entry => this.toSelectionSummary(entry.row));
		}

		const rowsWithCreatedAt = this.overviewRows
			.map(row => ({
				row,
				createdAt: this.parseComparableDate(row.latestSourceCreatedAtUtc),
			}))
			.filter((entry): entry is { row: ChangelogReleaseOverviewRow; createdAt: string } => entry.createdAt != null);
		if (rowsWithCreatedAt.length === 0) {
			return [];
		}

		const latestCreatedAt = rowsWithCreatedAt.reduce(
			(latest, entry) => entry.createdAt > latest ? entry.createdAt : latest,
			rowsWithCreatedAt[0].createdAt,
		);

		return rowsWithCreatedAt
			.filter(entry => entry.createdAt === latestCreatedAt)
			.map(entry => this.toSelectionSummary(entry.row));
	}

	private parseComparableDate(value?: string | null): string | null {
		const trimmedValue = value?.trim();
		if (!trimmedValue) {
			return null;
		}

		const isoDateMatch = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
		if (isoDateMatch != null) {
			return `${isoDateMatch[1]}-${isoDateMatch[2]}-${isoDateMatch[3]}`;
		}

		const parsedValue = new Date(trimmedValue);
		if (Number.isNaN(parsedValue.getTime())) {
			return null;
		}

		const year = parsedValue.getFullYear();
		const month = String(parsedValue.getMonth() + 1).padStart(2, '0');
		const day = String(parsedValue.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}

	private getSelectedValues(event: Event): string[] {
		const target = event.target as HTMLSelectElement | null;
		if (target == null) {
			return [];
		}

		return Array.from(target.selectedOptions).map(option => option.value);
	}

	private areSelectionKeysEqual(left: string[], right: string[]): boolean {
		if (left.length !== right.length) {
			return false;
		}

		return left.every((value, index) => value === right[index]);
	}

	private confirmDiscardChanges(): boolean {
		return globalThis.confirm == null
			? true
			: globalThis.confirm('Discard unsaved grouped changelog draft changes?');
	}

	private confirmRebuildFromSourceFiles(): boolean {
		return globalThis.confirm == null
			? true
			: globalThis.confirm('Rebuild the draft from the selected source files? Existing note edits will be replaced, but release details will stay as-is.');
	}

	private confirmOpenRelease(selections: ReleaseSelectionSummary[]): boolean {
		const targetRelease = selections[0];
		const releaseCount = selections.length;
		const message = releaseCount === 1
			? `Open ${targetRelease.displayTitle} for grouped changelog editing? The browser will be hidden until you choose another release.`
			: `Merge ${releaseCount} selected changelogs into ${targetRelease.displayTitle}? The first selection becomes the saved release and the browser will be hidden until you choose another selection.`;

		return globalThis.confirm == null
			? true
			: globalThis.confirm(message);
	}
}






